/**
 * DropsPagination
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { cn } from '@/lib/utils';

interface DropsPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function buildPageWindow(current: number, total: number): number[] {
  const maxButtons = 7;
  const half = Math.floor(maxButtons / 2);
  const start = clamp(current - half, 1, Math.max(1, total - maxButtons + 1));
  const end = Math.min(total, start + maxButtons - 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export const DropsPagination: FC<DropsPaginationProps> = ({
  page,
  totalPages,
  onPageChange,
}) => {
  const safeTotal = Math.max(1, totalPages);
  const safePage = clamp(page, 1, safeTotal);
  const pages = useMemo(
    () => buildPageWindow(safePage, safeTotal),
    [safePage, safeTotal],
  );
  const [jumpDraft, setJumpDraft] = useState(String(safePage));

  useEffect(() => {
    setJumpDraft(String(safePage));
  }, [safePage]);

  if (safeTotal <= 1) return null;

  const commitJump = () => {
    const parsed = Number.parseInt(jumpDraft, 10);
    if (!Number.isFinite(parsed)) {
      setJumpDraft(String(safePage));
      return;
    }
    onPageChange(clamp(parsed, 1, safeTotal));
  };

  return (
    <div className="flex flex-col gap-3 p-3 bg-muted/20 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-xs text-muted-foreground tabular-nums">
        Page <span className="font-medium text-foreground">{safePage}</span> of{' '}
        <span className="font-medium text-foreground">{safeTotal}</span>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-9 shrink-0 px-0"
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex min-w-[12.75rem] items-center justify-center gap-1">
          {pages.map((p) => {
            const active = p === safePage;
            return (
              <Button
                key={p}
                type="button"
                variant={active ? 'default' : 'outline'}
                size="sm"
                className={cn('w-9 shrink-0 px-0 tabular-nums', active && 'pointer-events-none')}
                onClick={() => onPageChange(p)}
                aria-label={`Go to page ${p}`}
                aria-current={active ? 'page' : undefined}
              >
                {p}
              </Button>
            );
          })}
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-9 shrink-0 px-0"
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage >= safeTotal}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        <div className="ml-1 flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground whitespace-nowrap">Go to</span>
          <Input
            type="number"
            min={1}
            max={safeTotal}
            value={jumpDraft}
            onChange={(e) => setJumpDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitJump();
            }}
            onBlur={commitJump}
            className="h-8 w-14 px-2 text-center text-xs tabular-nums"
            aria-label="Jump to page"
          />
        </div>
      </div>
    </div>
  );
};

export default DropsPagination;
